import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Play, Database, Download, Save, SaveAll, Check, AlignLeft, Zap, Copy, Scissors, ClipboardPaste, Type, BookOpen, BarChart3, Table2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Editor, { type OnMount, type Monaco } from '@monaco-editor/react';
import type { editor, IDisposable, Position } from 'monaco-editor';
import { AgGridReact } from 'ag-grid-react';
import { ClientSideRowModelModule, ModuleRegistry } from 'ag-grid-community';
import type { ColDef, CellStyle } from 'ag-grid-community';
import { apiClient } from '@/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { EDITOR_THEMES, GRID_THEMES } from '@/config/themes';
import { ExportModal } from '@/components/modals/ExportModal';
import { QueryPlanView, canShowPlanView } from '@/components/workarea/QueryPlanView';
import { ContextMenu, type ContextMenuItem } from '@/components/shared/ContextMenu';
import { SnippetPicker } from '@/components/shared/SnippetPicker';
import { JsonViewerModal } from '@/components/shared/JsonViewerModal';
import { JsonChipRenderer } from './JsonChipRenderer';
import { getEditorLanguage, isDocumentDriver } from '@/lib/driver-kind';
import type { QueryEditorTab, QueryResult, StatementResult, MultiStatementResponse, MultiStatementResultItem, SchemaMetadata, TableMeta, ConnectionConfig, DatabaseInfo } from '@/types';

ModuleRegistry.registerModules([ClientSideRowModelModule]);


const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'EXISTS',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE',
  'CREATE', 'ALTER', 'DROP', 'TABLE', 'INDEX', 'VIEW',
  'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'CROSS', 'FULL',
  'ON', 'AS', 'DISTINCT', 'ALL', 'UNION', 'INTERSECT', 'EXCEPT',
  'ORDER', 'BY', 'ASC', 'DESC', 'GROUP', 'HAVING',
  'LIMIT', 'OFFSET', 'FETCH', 'NEXT', 'ROWS', 'ONLY',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'NULL', 'IS', 'LIKE', 'ILIKE', 'BETWEEN', 'TRUE', 'FALSE',
  'COUNT', 'SUM', 'AVG', 'MIN', 'MAX',
  'COALESCE', 'NULLIF', 'CAST',
  'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'CONSTRAINT',
  'BEGIN', 'COMMIT', 'ROLLBACK', 'TRANSACTION',
  'GRANT', 'REVOKE', 'WITH', 'RECURSIVE',
  'EXPLAIN', 'ANALYZE', 'VACUUM', 'TRUNCATE',
];

/** Keywords that expect a table/view name to follow */
const TABLE_CONTEXT_KEYWORDS = new Set([
  'FROM', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'CROSS', 'FULL', 'INTO', 'UPDATE', 'TABLE',
]);

/**
 * Find the word immediately before the cursor position (skipping whitespace).
 * Returns the uppercase version for matching against SQL keywords.
 */
function getWordBeforeCursor(textBeforeCursor: string): string {
  const trimmed = textBeforeCursor.trimEnd();
  const match = trimmed.match(/(\w+)$/);
  return match ? match[1].toUpperCase() : '';
}

/**
 * Simple extraction of table names from the SQL text.
 * Looks for "FROM tablename" and "JOIN tablename" patterns.
 */
function extractTableNamesFromSql(sql: string): string[] {
  const tableNames: string[] = [];
  const pattern = /(?:FROM|JOIN)\s+(\w+(?:\.\w+)?)/gi;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(sql)) !== null) {
    tableNames.push(m[1]);
  }
  return tableNames;
}

/**
 * Build a lookup map: lowercased table name -> TableMeta
 */
function buildTableLookup(schema: SchemaMetadata): Map<string, TableMeta> {
  const map = new Map<string, TableMeta>();
  for (const db of schema.databases) {
    for (const s of db.schemas) {
      for (const table of s.tables) {
        map.set(table.name.toLowerCase(), table);
        // Also index as schema.table
        map.set(`${s.name}.${table.name}`.toLowerCase(), table);
      }
    }
  }
  return map;
}

// ═══════════════════════════════════════════
// Completion builder helpers
// ═══════════════════════════════════════════

interface CompletionRange {
  startLineNumber: number;
  endLineNumber: number;
  startColumn: number;
  endColumn: number;
}

interface CompletionSuggestion {
  label: string;
  kind: number;
  detail?: string;
  insertText: string;
  range: CompletionRange;
  sortText?: string;
}

interface CompletionList {
  suggestions: CompletionSuggestion[];
}

/**
 * After a dot: "schema." -> suggest tables in that schema,
 * or "table." -> suggest columns of that table.
 */
function buildDotCompletions(
  monacoInstance: Monaco,
  schema: SchemaMetadata | undefined,
  prefix: string,
  range: CompletionRange,
): CompletionList {
  const suggestions: CompletionSuggestion[] = [];
  if (!schema) return { suggestions };

  for (const db of schema.databases) {
    for (const s of db.schemas) {
      // If prefix matches a schema name, suggest its tables and views
      if (s.name.toLowerCase() === prefix) {
        for (const table of s.tables) {
          suggestions.push({
            label: table.name,
            kind: monacoInstance.languages.CompletionItemKind.Struct,
            detail: `Table (${s.name})`,
            insertText: table.name,
            range,
            sortText: '0_' + table.name,
          });
        }
        for (const view of s.views) {
          suggestions.push({
            label: view.name,
            kind: monacoInstance.languages.CompletionItemKind.Reference,
            detail: `View (${s.name})`,
            insertText: view.name,
            range,
            sortText: '1_' + view.name,
          });
        }
      }

      // If prefix matches a table name, suggest its columns
      for (const table of s.tables) {
        if (table.name.toLowerCase() === prefix) {
          for (const col of table.columns) {
            suggestions.push({
              label: col.name,
              kind: monacoInstance.languages.CompletionItemKind.Field,
              detail: col.type + (col.isPrimaryKey ? ' (PK)' : '') + (col.isForeignKey ? ' (FK)' : ''),
              insertText: col.name,
              range,
              sortText: '0_' + col.name,
            });
          }
        }
      }
    }
  }

  return { suggestions };
}

/**
 * After FROM/JOIN: suggest table and view names.
 */
function buildTableSuggestions(
  monacoInstance: Monaco,
  schema: SchemaMetadata | undefined,
  range: CompletionRange,
): CompletionList {
  const suggestions: CompletionSuggestion[] = [];
  if (!schema) return { suggestions };

  for (const db of schema.databases) {
    for (const s of db.schemas) {
      for (const table of s.tables) {
        const qualifiedName = s.name === 'public' ? table.name : `${s.name}.${table.name}`;
        suggestions.push({
          label: qualifiedName,
          kind: monacoInstance.languages.CompletionItemKind.Struct,
          detail: `Table (${s.name}) ~${table.rowCountEstimate ?? '?'} rows`,
          insertText: qualifiedName,
          range,
          sortText: '0_' + table.name,
        });
      }
      for (const view of s.views) {
        const qualifiedName = s.name === 'public' ? view.name : `${s.name}.${view.name}`;
        suggestions.push({
          label: qualifiedName,
          kind: monacoInstance.languages.CompletionItemKind.Reference,
          detail: `View (${s.name})`,
          insertText: qualifiedName,
          range,
          sortText: '1_' + view.name,
        });
      }
    }
  }

  return { suggestions };
}

/**
 * After SELECT or comma: suggest columns from referenced tables, or all columns.
 */
function buildSelectSuggestions(
  monacoInstance: Monaco,
  schema: SchemaMetadata | undefined,
  fullSql: string,
  range: CompletionRange,
): CompletionList {
  const suggestions: CompletionSuggestion[] = [];

  // Always include SQL keywords with lower priority
  for (const kw of SQL_KEYWORDS) {
    suggestions.push({
      label: kw,
      kind: monacoInstance.languages.CompletionItemKind.Keyword,
      detail: 'SQL keyword',
      insertText: kw,
      range,
      sortText: '3_' + kw,
    });
  }

  if (!schema) return { suggestions };

  const tableLookup = buildTableLookup(schema);
  const referencedTableNames = extractTableNamesFromSql(fullSql);

  // If we found specific tables in the query, suggest their columns first
  if (referencedTableNames.length > 0) {
    for (const tableName of referencedTableNames) {
      const table = tableLookup.get(tableName.toLowerCase());
      if (table) {
        for (const col of table.columns) {
          suggestions.push({
            label: col.name,
            kind: monacoInstance.languages.CompletionItemKind.Field,
            detail: `${col.type} (${table.name})`,
            insertText: col.name,
            range,
            sortText: '0_' + col.name,
          });
        }
      }
    }
  }

  // Also suggest all columns with lower priority
  const addedColumns = new Set(
    referencedTableNames.length > 0
      ? suggestions
          .filter((s) => s.kind === monacoInstance.languages.CompletionItemKind.Field)
          .map((s) => s.label)
      : [],
  );

  for (const db of schema.databases) {
    for (const s of db.schemas) {
      for (const table of s.tables) {
        for (const col of table.columns) {
          if (!addedColumns.has(col.name)) {
            addedColumns.add(col.name);
            suggestions.push({
              label: col.name,
              kind: monacoInstance.languages.CompletionItemKind.Field,
              detail: `${col.type} (${table.name})`,
              insertText: col.name,
              range,
              sortText: '1_' + col.name,
            });
          }
        }
      }
    }
  }

  // Suggest table names too (for "SELECT t.* FROM table t" style)
  for (const db of schema.databases) {
    for (const s of db.schemas) {
      for (const table of s.tables) {
        suggestions.push({
          label: table.name,
          kind: monacoInstance.languages.CompletionItemKind.Struct,
          detail: `Table (${s.name})`,
          insertText: table.name,
          range,
          sortText: '2_' + table.name,
        });
      }
    }
  }

  return { suggestions };
}

/**
 * Default context: SQL keywords + table names + function names.
 */
function buildDefaultSuggestions(
  monacoInstance: Monaco,
  schema: SchemaMetadata | undefined,
  range: CompletionRange,
): CompletionList {
  const suggestions: CompletionSuggestion[] = [];

  // SQL keywords
  for (const kw of SQL_KEYWORDS) {
    suggestions.push({
      label: kw,
      kind: monacoInstance.languages.CompletionItemKind.Keyword,
      detail: 'SQL keyword',
      insertText: kw,
      range,
      sortText: '0_' + kw,
    });
  }

  if (!schema) return { suggestions };

  // Table names
  for (const db of schema.databases) {
    for (const s of db.schemas) {
      for (const table of s.tables) {
        const qualifiedName = s.name === 'public' ? table.name : `${s.name}.${table.name}`;
        suggestions.push({
          label: qualifiedName,
          kind: monacoInstance.languages.CompletionItemKind.Struct,
          detail: `Table (${s.name})`,
          insertText: qualifiedName,
          range,
          sortText: '1_' + table.name,
        });
      }

      // View names
      for (const view of s.views) {
        const qualifiedName = s.name === 'public' ? view.name : `${s.name}.${view.name}`;
        suggestions.push({
          label: qualifiedName,
          kind: monacoInstance.languages.CompletionItemKind.Reference,
          detail: `View (${s.name})`,
          insertText: qualifiedName,
          range,
          sortText: '1_' + view.name,
        });
      }

      // Function names
      for (const fn of s.functions) {
        suggestions.push({
          label: fn.name,
          kind: monacoInstance.languages.CompletionItemKind.Function,
          detail: `Function -> ${fn.returnType}`,
          insertText: fn.name + '()',
          range,
          sortText: '2_' + fn.name,
        });
      }
    }
  }

  return { suggestions };
}

// ═══════════════════════════════════════════
// SQL Formatter
// ═══════════════════════════════════════════

const FORMAT_KEYWORDS = new Set(SQL_KEYWORDS.map((k) => k.toUpperCase()));

/**
 * Simple SQL formatter: uppercases keywords, adds newlines before major
 * clauses, and indents AND/OR with 2 spaces.
 */
function formatSql(sql: string): string {
  // Tokenize preserving strings and identifiers
  const tokens: string[] = [];
  let remaining = sql;
  while (remaining.length > 0) {
    // Single-quoted string
    const sq = remaining.match(/^('(?:[^'\\]|\\.)*')/);
    if (sq) {
      tokens.push(sq[1]);
      remaining = remaining.slice(sq[1].length);
      continue;
    }
    // Double-quoted identifier
    const dq = remaining.match(/^("(?:[^"\\]|\\.)*")/);
    if (dq) {
      tokens.push(dq[1]);
      remaining = remaining.slice(dq[1].length);
      continue;
    }
    // Whitespace
    const ws = remaining.match(/^(\s+)/);
    if (ws) {
      tokens.push(' ');
      remaining = remaining.slice(ws[1].length);
      continue;
    }
    // Word
    const word = remaining.match(/^(\w+)/);
    if (word) {
      const upper = word[1].toUpperCase();
      tokens.push(FORMAT_KEYWORDS.has(upper) ? upper : word[1]);
      remaining = remaining.slice(word[1].length);
      continue;
    }
    // Other characters (operators, punctuation)
    tokens.push(remaining[0]);
    remaining = remaining.slice(1);
  }

  // Rejoin and collapse whitespace
  let formatted = tokens.join('').replace(/ +/g, ' ').trim();

  // Add newlines before major clauses
  const clauseKeywords = [
    'FROM',
    'WHERE',
    'INNER JOIN',
    'LEFT JOIN',
    'RIGHT JOIN',
    'CROSS JOIN',
    'FULL JOIN',
    'JOIN',
    'ORDER BY',
    'GROUP BY',
    'HAVING',
    'LIMIT',
    'UNION',
  ];
  for (const clause of clauseKeywords) {
    // Use word-boundary-aware replacement so we don't break partial words
    const re = new RegExp(`\\s+${clause.replace(/ /g, '\\s+')}\\b`, 'gi');
    formatted = formatted.replace(re, `\n${clause}`);
  }

  // Indent AND/OR with 2 spaces
  formatted = formatted.replace(/\n?(AND|OR)\b/gi, (_match, kw: string) => `\n  ${kw.toUpperCase()}`);

  return formatted;
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

interface QueryEditorProps {
  tab: QueryEditorTab;
}

/**
 * Check if SQL contains multiple statements by looking for semicolons
 * outside of quoted strings and comments.
 */
function hasMultipleStatements(sql: string): boolean {
  let inSingleQuote = false;
  let inDoubleQuote = false;
  let inLineComment = false;
  let inBlockComment = false;
  let foundSemicolon = false;

  for (let i = 0; i < sql.length; i++) {
    const ch = sql[i];
    const next = sql[i + 1];

    if (inLineComment) {
      if (ch === '\n') inLineComment = false;
      continue;
    }
    if (inBlockComment) {
      if (ch === '*' && next === '/') { inBlockComment = false; i++; }
      continue;
    }
    if (ch === '-' && next === '-' && !inSingleQuote && !inDoubleQuote) { inLineComment = true; continue; }
    if (ch === '/' && next === '*' && !inSingleQuote && !inDoubleQuote) { inBlockComment = true; continue; }
    if (ch === "'" && !inDoubleQuote) { inSingleQuote = !inSingleQuote; continue; }
    if (ch === '"' && !inSingleQuote) { inDoubleQuote = !inDoubleQuote; continue; }
    if (ch === ';' && !inSingleQuote && !inDoubleQuote) {
      // Check if there's meaningful text after this semicolon
      if (foundSemicolon) return true;
      const remaining = sql.slice(i + 1).trim();
      if (remaining.length > 0) return true;
      foundSemicolon = true;
    }
  }
  return false;
}

export function QueryEditor({ tab }: QueryEditorProps) {
  const updateTab = useWorkspaceStore((s) => s.updateTab);
  const queryClient = useQueryClient();

  // Settings-driven editor options
  const editorFontSize = useSettingsStore((s) => s.getNumber('editor.fontSize', 12.5));
  const editorFontFamily = useSettingsStore((s) => s.get('editor.fontFamily', "'JetBrains Mono', monospace"));
  const editorLineHeight = useSettingsStore((s) => s.getNumber('editor.lineHeight', 1.65));
  const editorMinimap = useSettingsStore((s) => s.getBool('editor.minimap', false));
  const editorWordWrap = useSettingsStore((s) => s.get('editor.wordWrap', 'on'));
  const editorLineNumbers = useSettingsStore((s) => s.get('editor.lineNumbers', 'on'));
  const editorTabSize = useSettingsStore((s) => s.getNumber('editor.tabSize', 2));
  const appTheme = useAppStore((s) => s.theme);
  const gridThemeId = useSettingsStore((s) =>
    s.get(appTheme === 'dark' ? 'grid.theme.dark' : 'grid.theme.light',
          appTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light'));
  const gridTheme = GRID_THEMES.find(t => t.id === gridThemeId) ?? GRID_THEMES[0];
  const gridFontFamily = useSettingsStore((s) => s.get('grid.fontFamily', "'JetBrains Mono', monospace"));
  const gridFontSize = useSettingsStore((s) => s.getNumber('grid.fontSize', 12));
  const gridRowHeight = useSettingsStore((s) => s.getNumber('grid.rowHeight', 30));
  const gridHeaderHeight = useSettingsStore((s) => s.getNumber('grid.headerHeight', 32));
  const gridCellPadding = useSettingsStore((s) => s.getNumber('grid.cellPadding', 12));
  const editorThemeId = useSettingsStore((s) =>
    s.get(appTheme === 'dark' ? 'editor.theme.dark' : 'editor.theme.light',
          appTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light')
  );
  const [editorSql, setEditorSql] = useState(tab.sql);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [multiResults, setMultiResults] = useState<MultiStatementResultItem[] | null>(null);
  // Open `JsonViewerModal` when a chip is double-clicked. Shared with
  // the multi-result + single-result grids via cellRendererParams.
  const [jsonViewer, setJsonViewer] = useState<{ value: unknown; title: string } | null>(null);
  const [activeResultTab, setActiveResultTab] = useState(0);
  const [totalDurationMs, setTotalDurationMs] = useState<number | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [splitRatio, setSplitRatio] = useState(0.5);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [editorContextMenu, setEditorContextMenu] = useState<{ x: number; y: number } | null>(null);
  const completionDisposableRef = useRef<IDisposable | null>(null);
  const mongoCompletionDisposableRef = useRef<IDisposable | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const editorInstanceRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  // Save dialog state
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveInputValue, setSaveInputValue] = useState('');
  const [saveMode, setSaveMode] = useState<'save' | 'saveAs'>('save');
  const saveInputRef = useRef<HTMLInputElement>(null);

  // Saved feedback
  const [showSavedFeedback, setShowSavedFeedback] = useState(false);

  // Plan view toggle state
  const [showPlanView, setShowPlanView] = useState(false);

  // Snippet picker state
  const [snippetOpen, setSnippetOpen] = useState(false);

  // Title rename state
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Fetch connections list to resolve connection name
  const { data: connections } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
  });
  const connection = connections?.find((c) => c.id === tab.connectionId);

  // Databases list for the active-database dropdown. The execute endpoint
  // already honors `tab.database`, but until now nothing in the UI let
  // the user pick it — the badge top-right was display-only and lied
  // when the tab was running against a non-default DB.
  const { data: databases } = useQuery<DatabaseInfo[]>({
    queryKey: ['databases', tab.connectionId],
    queryFn: () => apiClient.get<DatabaseInfo[]>(`/api/connections/${tab.connectionId}/databases`),
    staleTime: 60_000,
  });

  /** The DB queries are actually routed to. Undefined on `tab.database`
   *  means "use the connection's default" — surface the real name so the
   *  badge can show something meaningful. */
  const activeDatabase = tab.database ?? connection?.databaseName ?? '';
  const isOverridingDefault = !!tab.database && tab.database !== connection?.databaseName;

  /** Track the DB the latest result came from so a switch followed by
   *  scanning the still-on-screen rows can't quietly mislead the user.
   *  Cleared on each new result. */
  const [resultDatabase, setResultDatabase] = useState<string | undefined>(undefined);

  // Database switcher dropdown
  const [showDbMenu, setShowDbMenu] = useState(false);
  const dbMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showDbMenu) return;
    const onClickOutside = (e: MouseEvent) => {
      if (dbMenuRef.current && !dbMenuRef.current.contains(e.target as Node)) {
        setShowDbMenu(false);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowDbMenu(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [showDbMenu]);

  const handleSwitchDatabase = useCallback((dbName: string) => {
    // Special case: picking the connection's default clears the override
    // so the tab is "default-tracking" again — e.g., changing the
    // connection's default later will follow.
    const isDefault = connection?.databaseName === dbName;
    updateTab(tab.id, { database: isDefault ? undefined : dbName });
    setShowDbMenu(false);
  }, [connection?.databaseName, tab.id, updateTab]);

  // Fetch schema metadata for auto-completion
  const { data: schemaData } = useQuery<SchemaMetadata>({
    queryKey: ['schema', tab.connectionId],
    queryFn: () => apiClient.get<SchemaMetadata>(`/api/schema/${tab.connectionId}`),
    staleTime: 60_000,
  });

  // Keep schema ref current so the completion provider always sees latest data
  const schemaRef = useRef<SchemaMetadata | undefined>(schemaData);
  schemaRef.current = schemaData;

  // --- Save mutations ---
  const createSavedQuery = useMutation({
    mutationFn: (payload: { connectionId: string; title: string; sql: string }) =>
      apiClient.post<{ id: string }>('/api/saved-queries', payload),
    onSuccess: (data, variables) => {
      updateTab(tab.id, { savedQueryId: data.id, title: variables.title });
      queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
      flashSavedFeedback();
    },
  });

  const updateSavedQuery = useMutation({
    mutationFn: (payload: { id: string; title?: string; sql?: string }) => {
      const { id, ...body } = payload;
      return apiClient.put<{ ok: boolean }>(`/api/saved-queries/${id}`, body);
    },
    onSuccess: (_data, variables) => {
      if (variables.title) {
        updateTab(tab.id, { title: variables.title });
      }
      queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
      flashSavedFeedback();
    },
  });

  const flashSavedFeedback = useCallback(() => {
    setShowSavedFeedback(true);
    setTimeout(() => setShowSavedFeedback(false), 1500);
  }, []);

  // Save handler (Ctrl+S)
  const handleSave = useCallback(() => {
    if (tab.savedQueryId) {
      // Update existing
      updateSavedQuery.mutate({ id: tab.savedQueryId, sql: editorSql });
    } else {
      // Open save dialog
      setSaveMode('save');
      setSaveInputValue(tab.title || '');
      setShowSaveInput(true);
    }
  }, [tab.savedQueryId, tab.title, editorSql, updateSavedQuery]);

  // Save As handler (Ctrl+Shift+S)
  const handleSaveAs = useCallback(() => {
    setSaveMode('saveAs');
    setSaveInputValue(tab.title || '');
    setShowSaveInput(true);
  }, [tab.title]);

  // Confirm save dialog
  const handleSaveConfirm = useCallback(() => {
    const title = saveInputValue.trim();
    if (!title) return;
    setShowSaveInput(false);
    createSavedQuery.mutate({
      connectionId: tab.connectionId,
      title,
      sql: editorSql,
    });
  }, [saveInputValue, tab.connectionId, editorSql, createSavedQuery]);

  // Cancel save dialog
  const handleSaveCancel = useCallback(() => {
    setShowSaveInput(false);
    setSaveInputValue('');
  }, []);

  // Focus save input when shown
  useEffect(() => {
    if (showSaveInput) {
      // setTimeout ensures the input is rendered before focusing
      setTimeout(() => saveInputRef.current?.focus(), 0);
    }
  }, [showSaveInput]);

  // Title rename handlers
  const handleStartRename = useCallback(() => {
    if (!tab.savedQueryId) return;
    setRenameValue(tab.title || '');
    setIsRenamingTitle(true);
    setTimeout(() => renameInputRef.current?.focus(), 0);
  }, [tab.savedQueryId, tab.title]);

  const handleRenameConfirm = useCallback(() => {
    const title = renameValue.trim();
    if (!title || !tab.savedQueryId) {
      setIsRenamingTitle(false);
      return;
    }
    setIsRenamingTitle(false);
    updateSavedQuery.mutate({ id: tab.savedQueryId, title });
  }, [renameValue, tab.savedQueryId, updateSavedQuery]);

  const handleRenameCancel = useCallback(() => {
    setIsRenamingTitle(false);
    setRenameValue('');
  }, []);

  // Get the currently active query result (single or from multi-statement)
  const activeQueryResult = useMemo<QueryResult | null>(() => {
    if (result) return result;
    if (multiResults && multiResults[activeResultTab]?.type === 'query') {
      return multiResults[activeResultTab].result as QueryResult;
    }
    return null;
  }, [result, multiResults, activeResultTab]);

  // Detect if the active result looks like EXPLAIN output
  const isExplainResult = useMemo<boolean>(() => {
    if (!activeQueryResult) return false;
    return canShowPlanView(activeQueryResult);
  }, [activeQueryResult]);

  // Keep editorSql in sync when tab.sql changes externally (e.g. hydration)
  const prevTabSqlRef = useRef(tab.sql);
  useEffect(() => {
    if (tab.sql !== prevTabSqlRef.current) {
      setEditorSql(tab.sql);
      prevTabSqlRef.current = tab.sql;
    }
  }, [tab.sql]);

  const executeMutation = useMutation({
    mutationFn: (sql: string) =>
      apiClient.post<QueryResult>('/api/query/execute', {
        connectionId: tab.connectionId,
        database: tab.database,
        sql,
        page: 1,
        pageSize: 500,
      }),
    onSuccess: (data) => {
      setResult(data);
      setMultiResults(null);
      setTotalDurationMs(null);
      setQueryError(null);
      setResultDatabase(activeDatabase);
      useConnectionStore.getState().setStatus(tab.connectionId, 'connected');
    },
    onError: (err) => {
      setResult(null);
      setMultiResults(null);
      setTotalDurationMs(null);
      setQueryError(err instanceof Error ? err.message : 'Query execution failed');
    },
  });

  const executeMultiMutation = useMutation({
    mutationFn: (sql: string) =>
      apiClient.post<MultiStatementResponse>('/api/query/execute-multi', {
        connectionId: tab.connectionId,
        database: tab.database,
        sql,
      }),
    onSuccess: (data) => {
      setMultiResults(data.results);
      setTotalDurationMs(data.totalDurationMs);
      setActiveResultTab(0);
      setResult(null);
      setQueryError(null);
      setResultDatabase(activeDatabase);
      useConnectionStore.getState().setStatus(tab.connectionId, 'connected');
    },
    onError: (err) => {
      setResult(null);
      setMultiResults(null);
      setTotalDurationMs(null);
      setQueryError(err instanceof Error ? err.message : 'Query execution failed');
    },
  });

  const isExecuting = executeMutation.isPending || executeMultiMutation.isPending;

  const handleRun = useCallback(() => {
    const sql = editorSql.trim();
    if (!sql) return;
    if (hasMultipleStatements(sql)) {
      executeMultiMutation.mutate(sql);
    } else {
      executeMutation.mutate(sql);
    }
  }, [editorSql, executeMutation, executeMultiMutation]);

  const handleEditorContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setEditorContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleFormat = useCallback(() => {
    // SQL drivers use our SQL formatter; document drivers fall back to
    // Monaco's built-in javascript formatter (Prettier-light).
    if (isDocumentDriver(connection?.driverType ?? 'postgresql')) {
      editorInstanceRef.current?.getAction('editor.action.formatDocument')?.run();
      return;
    }
    const formatted = formatSql(editorSql);
    setEditorSql(formatted);
    updateTab(tab.id, { sql: formatted });
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setValue(formatted);
    }
  }, [editorSql, tab.id, updateTab, connection?.driverType]);

  // Command-bar hooks: respond to `dbgo:query:*` custom events only when
  // this tab is the focused one. That way multiple query tabs in split
  // panes don't all fire when the user runs one command.
  useEffect(() => {
    function isActive(): boolean {
      return useWorkspaceStore.getState().activeTabId === tab.id;
    }
    const onRun = () => { if (isActive()) handleRun(); };
    const onFormat = () => { if (isActive()) handleFormat(); };
    const onSave = () => { if (isActive()) handleSave(); };
    const onSaveAs = () => { if (isActive()) handleSaveAs(); };
    const onSnippets = () => {
      if (isActive()) setSnippetOpen(true);
    };
    window.addEventListener('dbgo:query:run', onRun);
    window.addEventListener('dbgo:query:format', onFormat);
    window.addEventListener('dbgo:query:save', onSave);
    window.addEventListener('dbgo:query:save-as', onSaveAs);
    window.addEventListener('dbgo:query:snippets', onSnippets);
    return () => {
      window.removeEventListener('dbgo:query:run', onRun);
      window.removeEventListener('dbgo:query:format', onFormat);
      window.removeEventListener('dbgo:query:save', onSave);
      window.removeEventListener('dbgo:query:save-as', onSaveAs);
      window.removeEventListener('dbgo:query:snippets', onSnippets);
    };
  }, [tab.id, handleRun, handleFormat, handleSave, handleSaveAs]);

  const handleExplain = useCallback(() => {
    const sql = editorSql.trim();
    if (!sql) return;
    // Use JSON format for PostgreSQL to enable visual plan rendering
    const isPostgres = connection?.driverType === 'postgresql';
    const explainSql = isPostgres
      ? `EXPLAIN (ANALYZE, FORMAT JSON) ${sql}`
      : `EXPLAIN ANALYZE ${sql}`;
    setShowPlanView(true);
    executeMutation.mutate(explainSql);
  }, [editorSql, executeMutation, connection?.driverType]);

  const handleSnippetSelect = useCallback(
    (sql: string) => {
      const ed = editorInstanceRef.current;
      if (ed) {
        const model = ed.getModel();
        const currentValue = model?.getValue() ?? '';
        if (!currentValue.trim()) {
          // Editor is empty: replace content
          ed.setValue(sql);
        } else {
          // Insert at cursor position
          const selection = ed.getSelection();
          if (selection) {
            ed.executeEdits('snippet', [
              { range: selection, text: sql, forceMoveMarkers: true },
            ]);
          }
        }
        ed.focus();
        // Sync state
        const newValue = ed.getValue();
        setEditorSql(newValue);
        updateTab(tab.id, { sql: newValue });
      }
    },
    [tab.id, updateTab],
  );

  const editorContextMenuItems = useMemo<ContextMenuItem[]>(() => {
    const ed = editorInstanceRef.current;
    const hasSelection = !!ed?.getSelection()?.isEmpty() === false;
    return [
      {
        label: 'Cut',
        icon: Scissors,
        disabled: !hasSelection,
        onClick: () => { ed?.focus(); ed?.trigger('menu', 'editor.action.clipboardCutAction', null); },
      },
      {
        label: 'Copy',
        icon: Copy,
        disabled: !hasSelection,
        onClick: () => { ed?.focus(); ed?.trigger('menu', 'editor.action.clipboardCopyAction', null); },
      },
      {
        label: 'Paste',
        icon: ClipboardPaste,
        onClick: () => { ed?.focus(); ed?.trigger('menu', 'editor.action.clipboardPasteAction', null); },
      },
      {
        label: 'Select All',
        icon: Type,
        separator: true,
        onClick: () => { ed?.focus(); ed?.trigger('menu', 'editor.action.selectAll', null); },
      },
      {
        label: 'Format SQL',
        icon: AlignLeft,
        separator: true,
        onClick: () => handleFormat(),
      },
      {
        label: 'Run Query',
        icon: Play,
        disabled: !editorSql.trim(),
        onClick: () => handleRun(),
      },
      {
        label: 'Explain Query',
        icon: Zap,
        disabled: !editorSql.trim(),
        onClick: () => handleExplain(),
      },
    ];
  }, [editorSql, handleFormat, handleRun, handleExplain]);

  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      const newSql = value ?? '';
      setEditorSql(newSql);
      updateTab(tab.id, { sql: newSql });
    },
    [tab.id, updateTab],
  );

  /** Mongosh-flavoured completion items. Suggests collection names from
   *  the schema after `db.`, the v1 op surface after `db.<coll>.`, and
   *  common operators after `$`. Fires only on `javascript` models so
   *  it doesn't pollute SQL editors. */
  const registerMongoProvider = useCallback((monacoInstance: Monaco) => {
    mongoCompletionDisposableRef.current?.dispose();

    const OPS = [
      'find', 'findOne', 'countDocuments', 'aggregate', 'distinct',
      'getIndexes', 'stats', 'estimatedDocumentCount',
      'insertOne', 'insertMany', 'updateOne', 'updateMany',
      'deleteOne', 'deleteMany',
    ];
    const OPERATORS = [
      '$eq', '$ne', '$gt', '$gte', '$lt', '$lte',
      '$in', '$nin', '$exists', '$type', '$regex',
      '$and', '$or', '$not', '$nor',
      '$set', '$unset', '$inc', '$push', '$pull', '$addToSet',
      '$match', '$group', '$project', '$sort', '$limit', '$skip', '$lookup', '$unwind',
    ];

    const disposable = monacoInstance.languages.registerCompletionItemProvider('javascript', {
      triggerCharacters: ['.', '$'],
      provideCompletionItems: (model: editor.ITextModel, position: Position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };
        const before = model.getValueInRange({
          startLineNumber: 1, startColumn: 1,
          endLineNumber: position.lineNumber, endColumn: position.column,
        });

        // `db.<collection>.` → operation stubs.
        const collOpMatch = before.match(/db\.([A-Za-z_$][\w$]*)\.\s*$/);
        if (collOpMatch) {
          return {
            suggestions: OPS.map((op) => ({
              label: op,
              kind: monacoInstance.languages.CompletionItemKind.Method,
              insertText: `${op}($1)`,
              insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range,
              detail: 'mongosh',
            })),
          };
        }

        // `db.` → collection names from inferred schema.
        if (/\bdb\.\s*$/.test(before)) {
          const schema = schemaRef.current;
          const collections = schema?.databases?.[0]?.schemas?.[0]?.tables ?? [];
          return {
            suggestions: collections.map((c) => ({
              label: c.name,
              kind: monacoInstance.languages.CompletionItemKind.Class,
              insertText: c.name,
              range,
              detail: 'collection',
            })),
          };
        }

        // After `$` (in word or just typed) → operator stubs.
        if (word.word.startsWith('$') || /\$\s*$/.test(before)) {
          return {
            suggestions: OPERATORS.map((op) => ({
              label: op,
              kind: monacoInstance.languages.CompletionItemKind.Operator,
              insertText: op,
              range,
              detail: 'mongo operator',
            })),
          };
        }

        // Default: top-level mongosh globals.
        return {
          suggestions: [
            { label: 'db', kind: monacoInstance.languages.CompletionItemKind.Variable, insertText: 'db', range, detail: 'mongosh' },
            { label: 'ObjectId', kind: monacoInstance.languages.CompletionItemKind.Function, insertText: 'ObjectId("$1")', insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet, range, detail: 'BSON' },
            { label: 'ISODate', kind: monacoInstance.languages.CompletionItemKind.Function, insertText: 'ISODate("$1")', insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet, range, detail: 'BSON' },
          ],
        };
      },
    });
    mongoCompletionDisposableRef.current = disposable;
  }, []);

  // Register completion provider
  const registerCompletionProvider = useCallback((monacoInstance: Monaco) => {
    // Dispose previous registration if any
    completionDisposableRef.current?.dispose();

    const disposable = monacoInstance.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: ['.', ' '],
      provideCompletionItems: (model: editor.ITextModel, position: Position) => {
        const schema = schemaRef.current;
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const textBeforeCursor = model.getValueInRange({
          startLineNumber: 1,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        const fullText = model.getValue();

        // Check if cursor is right after a dot (e.g., "schema." or "table.")
        const dotMatch = textBeforeCursor.match(/(\w+)\.\s*$/);
        if (dotMatch) {
          const prefix = dotMatch[1].toLowerCase();
          return buildDotCompletions(monacoInstance, schema, prefix, range);
        }

        // Check the word before the current typing position
        // We need the text before the current word being typed
        const textBeforeWord = textBeforeCursor.substring(0, textBeforeCursor.length - word.word.length);
        const precedingKeyword = getWordBeforeCursor(textBeforeWord);

        // After FROM, JOIN, etc. -> suggest tables and views
        if (TABLE_CONTEXT_KEYWORDS.has(precedingKeyword)) {
          return buildTableSuggestions(monacoInstance, schema, range);
        }

        // After SELECT -> suggest columns from tables in query + all columns + keywords
        if (precedingKeyword === 'SELECT' || precedingKeyword === ',') {
          return buildSelectSuggestions(monacoInstance, schema, fullText, range);
        }

        // Default: SQL keywords + all table names
        return buildDefaultSuggestions(monacoInstance, schema, range);
      },
    });

    completionDisposableRef.current = disposable;
  }, []);

  // Re-register when schema data changes so completions stay fresh.
  // The mongo provider also re-registers so newly-listed collections
  // appear in `db.<TAB>` completion.
  useEffect(() => {
    if (!monacoRef.current) return;
    registerCompletionProvider(monacoRef.current);
    if (isDocumentDriver(connection?.driverType ?? 'postgresql')) {
      registerMongoProvider(monacoRef.current);
    }
  }, [schemaData, registerCompletionProvider, registerMongoProvider, connection?.driverType]);

  // Clean up completion providers on unmount
  useEffect(() => {
    return () => {
      completionDisposableRef.current?.dispose();
      mongoCompletionDisposableRef.current?.dispose();
    };
  }, []);

  // Keep refs for handlers so editor actions always see latest
  const handleRunRef = useRef(handleRun);
  handleRunRef.current = handleRun;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const handleSaveAsRef = useRef(handleSaveAs);
  handleSaveAsRef.current = handleSaveAs;

  const handleEditorMount: OnMount = useCallback(
    (editorInstance, monacoInstance) => {
      monacoRef.current = monacoInstance;
      editorInstanceRef.current = editorInstance;

      // Register all editor themes
      for (const theme of EDITOR_THEMES) {
        monacoInstance.editor.defineTheme(theme.id, theme.monaco);
      }
      monacoInstance.editor.setTheme(editorThemeId);

      // Register completion providers. SQL provider scopes to the `sql`
      // language; Mongo provider scopes to `javascript`. Both can be
      // live at once across multiple editor instances — Monaco routes
      // by the model's language id.
      registerCompletionProvider(monacoInstance);

      // For document drivers, light up the JS service for syntax-only
      // benefits (bracket/string matching) and our mongosh-aware
      // completion. Turn off semantic diagnostics — otherwise Monaco
      // squigglies every mongosh global (`db`, `ObjectId`, `ISODate`)
      // as undeclared.
      if (isDocumentDriver(connection?.driverType ?? 'postgresql')) {
        try {
          monacoInstance.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: false,
            noSuggestionDiagnostics: true,
          });
        } catch { /* monaco-typescript may not be loaded in some builds */ }
        registerMongoProvider(monacoInstance);
      }

      // Ctrl/Cmd+Enter to run
      editorInstance.addAction({
        id: 'dbgo-run-query',
        label: 'Run Query',
        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.Enter],
        run: () => {
          handleRunRef.current();
        },
      });

      // Ctrl/Cmd+S to save
      editorInstance.addAction({
        id: 'dbgo-save-query',
        label: 'Save Query',
        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyCode.KeyS],
        run: () => {
          handleSaveRef.current();
        },
      });

      // Ctrl/Cmd+Shift+S to save as
      editorInstance.addAction({
        id: 'dbgo-save-as-query',
        label: 'Save Query As',
        keybindings: [monacoInstance.KeyMod.CtrlCmd | monacoInstance.KeyMod.Shift | monacoInstance.KeyCode.KeyS],
        run: () => {
          handleSaveAsRef.current();
        },
      });
    },
    [registerCompletionProvider, registerMongoProvider, connection?.driverType],
  );

  // Apply editor theme changes dynamically
  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco?.editor) {
      monaco.editor.setTheme(editorThemeId);
    }
  }, [editorThemeId]);

  // Resize handle for editor/results split
  const handleMouseDown = useCallback(() => {
    isDragging.current = true;
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const ratio = (e.clientY - rect.top) / rect.height;
      setSplitRatio(Math.min(0.85, Math.max(0.15, ratio)));
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    if (!activeQueryResult?.columns) return [];
    return activeQueryResult.columns.map((col): ColDef => ({
      field: col.name,
      headerName: col.name,
      resizable: true,
      sortable: true,
      minWidth: 80,
      cellStyle: (params): CellStyle | null | undefined => {
        if (params.value === null || params.value === undefined) {
          return { color: 'var(--text-muted)', fontStyle: 'italic', opacity: '0.6' };
        }
        if (typeof params.value === 'number') {
          return { color: '#d19a66' };
        }
        if (typeof params.value === 'boolean') {
          return { color: '#c678dd' };
        }
        return null;
      },
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return 'NULL';
        if (typeof params.value === 'object') return JSON.stringify(params.value);
        return String(params.value);
      },
      // Object / array cells render as a chip so JSONB + Mongo nested
      // fields stay readable. Double-click opens the shared JSON
      // viewer; valueFormatter still feeds clipboard / export.
      cellRenderer: JsonChipRenderer,
      cellRendererParams: {
        onOpen: (value: unknown, title: string) => setJsonViewer({ value, title }),
      },
    }));
  }, [activeQueryResult?.columns]);

  const hasResult = result !== null || multiResults !== null || queryError !== null;

  return (
    <div className="flex flex-col h-full bg-dbgo-workarea">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 h-[38px] bg-dbgo-panel border-b flex-shrink-0 relative">
        {/* Left: Run + Save buttons */}
        <button
          onClick={handleRun}
          disabled={isExecuting || !editorSql.trim()}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-accent-green bg-transparent border border-accent-green/30 rounded-sm hover:bg-accent-green/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Run query (Ctrl+Enter)"
        >
          <Play size={12} fill="currentColor" />
          Run
        </button>

        <div className="w-px h-4 bg-border-subtle mx-0.5" />

        <button
          onClick={handleSave}
          disabled={createSavedQuery.isPending || updateSavedQuery.isPending}
          className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-text-secondary bg-transparent border border-border-subtle rounded-sm hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Save (Ctrl+S)"
        >
          {showSavedFeedback ? <Check size={12} className="text-accent-green" /> : <Save size={12} />}
          {showSavedFeedback ? 'Saved' : 'Save'}
        </button>

        <button
          onClick={handleSaveAs}
          disabled={createSavedQuery.isPending}
          className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-text-secondary bg-transparent border border-border-subtle rounded-sm hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Save As (Ctrl+Shift+S)"
        >
          <SaveAll size={12} />
          Save As
        </button>

        <div className="w-px h-4 bg-border-subtle mx-0.5" />

        <button
          onClick={handleFormat}
          disabled={!editorSql.trim()}
          className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-text-secondary bg-transparent border border-border-subtle rounded-sm hover:bg-dbgo-tab-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Format SQL"
        >
          <AlignLeft size={12} />
          Format
        </button>

        <button
          onClick={handleExplain}
          disabled={isExecuting || !editorSql.trim()}
          className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-syn-type bg-transparent border border-syn-type/30 rounded-sm hover:bg-syn-type/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Run EXPLAIN ANALYZE"
        >
          <Zap size={12} />
          Explain
        </button>

        {/* Snippets */}
        <div className="relative">
          <button
            onClick={() => setSnippetOpen((v) => !v)}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-text-secondary bg-transparent border border-border-subtle rounded-sm hover:bg-dbgo-tab-hover transition-colors"
            title="Insert SQL snippet"
          >
            <BookOpen size={12} />
            Snippets
          </button>
          {snippetOpen && (
            <SnippetPicker
              onSelect={handleSnippetSelect}
              onClose={() => setSnippetOpen(false)}
            />
          )}
        </div>

        {/* Center: Query title */}
        <div className="flex-1 flex items-center justify-center min-w-0">
          {isRenamingTitle ? (
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameConfirm();
                if (e.key === 'Escape') handleRenameCancel();
              }}
              onBlur={handleRenameConfirm}
              className="bg-dbgo-input border rounded-sm px-2 py-0.5 text-xs text-text-primary outline-none text-center max-w-[200px] w-full"
            />
          ) : (
            <span
              className={`text-xs truncate max-w-[240px] ${
                tab.savedQueryId
                  ? 'text-text-primary cursor-pointer hover:text-accent-blue'
                  : 'text-text-muted italic'
              }`}
              onDoubleClick={handleStartRename}
              title={tab.savedQueryId ? 'Double-click to rename' : 'Unsaved query'}
            >
              {tab.savedQueryId ? tab.title : 'Untitled Query'}
            </span>
          )}
        </div>

        {/* Right: Connection badge with database switcher. The DB part is
            click-to-open — picks which database the next query runs against.
            A non-default DB tints the row so users notice they've drifted. */}
        <div ref={dbMenuRef} className="relative flex items-center gap-1.5 px-2 py-0.5 bg-dbgo-badge rounded-sm">
          <Database size={11} className="text-text-muted" />
          <span className="text-2xs text-text-primary font-mono">
            {connection?.name ?? tab.connectionId.slice(0, 8)}
          </span>
          {connection && (
            <span className="text-2xs text-text-muted font-mono">{connection.host}/</span>
          )}
          <button
            type="button"
            onClick={() => setShowDbMenu((v) => !v)}
            disabled={!databases || databases.length === 0}
            className={`text-2xs font-mono flex items-center gap-1 px-1 rounded-sm transition-colors ${
              isOverridingDefault
                ? 'text-accent-yellow hover:bg-accent-yellow/10'
                : 'text-text-primary hover:bg-dbgo-tab-hover'
            } disabled:opacity-60 disabled:cursor-default`}
            title={isOverridingDefault
              ? `Active: ${activeDatabase} (connection default: ${connection?.databaseName})`
              : 'Switch active database'}
          >
            {activeDatabase || '(default)'}
            <span className="text-text-muted">▾</span>
          </button>

          {showDbMenu && databases && databases.length > 0 && (
            <div className="absolute right-0 top-full mt-1 z-50 min-w-[200px] max-h-[320px] overflow-y-auto bg-dbgo-modal border rounded-sm shadow-lg">
              <div className="px-2.5 py-1.5 text-2xs text-text-muted border-b border-subtle">
                Run against
              </div>
              {databases.map((db) => {
                const isActive = db.name === activeDatabase;
                const isConnDefault = db.name === connection?.databaseName;
                return (
                  <button
                    key={db.name}
                    type="button"
                    onClick={() => handleSwitchDatabase(db.name)}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs text-left hover:bg-dbgo-tab-hover ${
                      isActive ? 'text-accent-blue' : 'text-text-primary'
                    }`}
                  >
                    <span className="font-mono truncate">{db.name}</span>
                    <span className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                      {isConnDefault && <span className="text-2xs text-text-muted">default</span>}
                      {isActive && <Check size={12} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Inline save dialog overlay */}
        {showSaveInput && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-dbgo-panel/95 backdrop-blur-sm px-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary whitespace-nowrap">
                {saveMode === 'saveAs' ? 'Save As:' : 'Save Query:'}
              </span>
              <input
                ref={saveInputRef}
                value={saveInputValue}
                onChange={(e) => setSaveInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveConfirm();
                  if (e.key === 'Escape') handleSaveCancel();
                }}
                placeholder="Query title..."
                className="bg-dbgo-input border rounded-sm px-2 py-1 text-xs text-text-primary outline-none w-[200px] focus:border-focus"
              />
              <button
                onClick={handleSaveConfirm}
                disabled={!saveInputValue.trim()}
                className="px-2.5 py-1 text-xs font-medium text-accent-green border border-accent-green/30 rounded-sm hover:bg-accent-green/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Save
              </button>
              <button
                onClick={handleSaveCancel}
                className="px-2.5 py-1 text-xs text-text-muted hover:text-text-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Editor + Results split */}
      <div ref={containerRef} className="flex-1 flex flex-col min-h-0">
        {/* Editor */}
        <div
          className="min-h-0 overflow-hidden"
          style={{ flex: hasResult ? `0 0 ${splitRatio * 100}%` : '1 1 auto' }}
          onContextMenu={handleEditorContextMenu}
        >
          <Editor
            language={getEditorLanguage(connection?.driverType ?? 'postgresql')}
            theme="vs-dark"
            value={editorSql}
            onChange={handleEditorChange}
            onMount={handleEditorMount}
            options={{
              minimap: { enabled: editorMinimap },
              fontSize: editorFontSize,
              lineHeight: editorLineHeight * editorFontSize,
              fontFamily: editorFontFamily,
              tabSize: editorTabSize,
              padding: { top: 8, bottom: 8 },
              scrollBeyondLastLine: false,
              renderLineHighlight: 'line',
              lineNumbers: editorLineNumbers as 'on' | 'off' | 'relative',
              lineNumbersMinChars: 3,
              glyphMargin: false,
              folding: true,
              wordWrap: editorWordWrap as 'on' | 'off' | 'wordWrapColumn' | 'bounded',
              automaticLayout: true,
              contextmenu: false, // We use our own context menu
              suggest: {
                showKeywords: false, // We provide our own keyword completions
              },
            }}
          />
        </div>

        {/* Resize handle */}
        {hasResult && (
          <div
            onMouseDown={handleMouseDown}
            className="h-[4px] bg-dbgo-panel border-y cursor-row-resize hover:bg-accent-blue/20 transition-colors flex-shrink-0"
          />
        )}

        {/* Results area */}
        {hasResult && (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Stale-results warning: surfaces when the user switched DB
                after running a query, so they don't read rows from one
                DB while thinking they're in another. Re-running clears it
                via resultDatabase = activeDatabase. */}
            {resultDatabase && resultDatabase !== activeDatabase && (
              <div className="flex items-center gap-2 px-3 py-1 bg-accent-yellow/10 border-b text-2xs text-accent-yellow flex-shrink-0">
                <span>Results below are from</span>
                <span className="font-mono">{resultDatabase}</span>
                <span>— re-run to refresh against</span>
                <span className="font-mono">{activeDatabase}</span>
              </div>
            )}
            {queryError ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-sm text-accent-red max-w-lg text-center">
                  {queryError}
                </div>
              </div>
            ) : multiResults ? (
              /* Multi-statement results */
              <>
                {/* Result sub-tabs */}
                <div className="flex items-center gap-0 bg-dbgo-panel border-b flex-shrink-0 overflow-x-auto">
                  {multiResults.map((item, idx) => {
                    const isActive = activeResultTab === idx;
                    const hasError = !!item.error;
                    let label: string;
                    if (item.type === 'query') {
                      const qr = item.result as QueryResult;
                      label = `Result ${idx + 1} (${qr.rowCount} rows)`;
                    } else {
                      const sr = item.result as StatementResult;
                      label = hasError
                        ? `Statement ${idx + 1} (error)`
                        : `Statement ${idx + 1} (${sr.affectedRows} affected)`;
                    }
                    return (
                      <button
                        key={idx}
                        onClick={() => setActiveResultTab(idx)}
                        className={`px-3 py-1.5 text-[11px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                          isActive
                            ? 'border-focus text-text-primary bg-dbgo-workarea'
                            : hasError
                            ? 'border-transparent text-accent-red hover:bg-dbgo-tab-hover'
                            : 'border-transparent text-text-secondary hover:bg-dbgo-tab-hover'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                  {totalDurationMs != null && (
                    <span className="ml-auto px-3 py-1.5 text-[11px] text-text-muted whitespace-nowrap">
                      Total: {totalDurationMs}ms
                    </span>
                  )}
                </div>
                {/* Active result content */}
                {multiResults[activeResultTab] && (() => {
                  const item = multiResults[activeResultTab];
                  if (item.error) {
                    return (
                      <div className="flex-1 flex flex-col items-center justify-center p-4 gap-2">
                        <div className="text-sm text-accent-red max-w-lg text-center">
                          {item.error}
                        </div>
                        <div className="text-[11px] text-text-muted font-mono max-w-lg text-center truncate">
                          {item.sql.slice(0, 200)}
                        </div>
                      </div>
                    );
                  }
                  if (item.type === 'query') {
                    const qr = item.result as QueryResult;
                    return (
                      <>
                        <div className={`flex-1 min-h-0 ${appTheme === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'} ag-theme-dbgo`} style={{ height: '100%', '--ag-font-family': gridFontFamily, '--ag-font-size': `${gridFontSize}px`, '--ag-row-height': `${gridRowHeight}px`, '--ag-header-height': `${gridHeaderHeight}px`, '--ag-cell-horizontal-padding': `${gridCellPadding}px`, '--ag-background-color': gridTheme.colors.background, '--ag-header-background-color': gridTheme.colors.headerBackground, '--ag-odd-row-background-color': gridTheme.colors.oddRowBackground, '--ag-row-hover-color': gridTheme.colors.rowHover, '--ag-selected-row-background-color': gridTheme.colors.selectedRow, '--ag-border-color': gridTheme.colors.border, '--ag-header-foreground-color': gridTheme.colors.headerForeground, '--ag-foreground-color': gridTheme.colors.foreground } as React.CSSProperties}>
                          <AgGridReact
                            rowData={qr.rows}
                            columnDefs={columnDefs}
                            suppressMovableColumns={false}
                            suppressCellFocus={false}
                            animateRows={false}
                          />
                        </div>
                        <div className="flex items-center justify-between px-3 h-[28px] bg-dbgo-panel border-t flex-shrink-0">
                          <div className="flex items-center gap-3 text-[11px] text-text-secondary">
                            <span>Rows: <span className="text-text-primary font-mono">{qr.rowCount}</span></span>
                            <span>Time: <span className="text-text-primary font-mono">{item.durationMs}ms</span></span>
                            {qr.columns.length > 0 && (
                              <span>Columns: <span className="text-text-primary font-mono">{qr.columns.length}</span></span>
                            )}
                          </div>
                          <button
                            onClick={() => setShowExportModal(true)}
                            disabled={!qr.rows.length}
                            className="flex items-center gap-1 text-[11px] text-text-secondary hover:text-text-primary disabled:opacity-30 rounded-sm px-1.5 py-0.5 hover:bg-dbgo-tab-hover transition-colors"
                            title="Export results"
                          >
                            <Download size={11} />
                            Export
                          </button>
                        </div>
                      </>
                    );
                  }
                  // Statement result (INSERT/UPDATE/DELETE)
                  const sr = item.result as StatementResult;
                  return (
                    <div className="flex-1 flex flex-col items-center justify-center p-4 gap-2">
                      <div className="text-sm text-accent-green">
                        {sr.affectedRows} row{sr.affectedRows !== 1 ? 's' : ''} affected
                      </div>
                      <div className="text-[11px] text-text-muted">
                        Completed in {item.durationMs}ms
                      </div>
                      <div className="text-[11px] text-text-muted font-mono max-w-lg text-center truncate">
                        {item.sql.slice(0, 200)}
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : result ? (
              /* Single-statement result */
              <>
                {/* Plan view toggle bar (only when result is an EXPLAIN) */}
                {isExplainResult && (
                  <div className="flex items-center gap-2 px-3 h-[30px] bg-dbgo-panel border-b flex-shrink-0">
                    <button
                      onClick={() => setShowPlanView(true)}
                      className={`flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-sm transition-colors ${
                        showPlanView
                          ? 'text-accent-blue bg-accent-blue/10'
                          : 'text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover'
                      }`}
                    >
                      <BarChart3 size={11} />
                      Visual Plan
                    </button>
                    <button
                      onClick={() => setShowPlanView(false)}
                      className={`flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-sm transition-colors ${
                        !showPlanView
                          ? 'text-accent-blue bg-accent-blue/10'
                          : 'text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover'
                      }`}
                    >
                      <Table2 size={11} />
                      Table View
                    </button>
                  </div>
                )}
                {/* Conditionally render plan view or grid */}
                {isExplainResult && showPlanView ? (
                  <QueryPlanView result={result} />
                ) : (
                  <div className={`flex-1 min-h-0 ${appTheme === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'} ag-theme-dbgo`} style={{ height: '100%', '--ag-font-family': gridFontFamily, '--ag-font-size': `${gridFontSize}px`, '--ag-row-height': `${gridRowHeight}px`, '--ag-header-height': `${gridHeaderHeight}px`, '--ag-cell-horizontal-padding': `${gridCellPadding}px`, '--ag-background-color': gridTheme.colors.background, '--ag-header-background-color': gridTheme.colors.headerBackground, '--ag-odd-row-background-color': gridTheme.colors.oddRowBackground, '--ag-row-hover-color': gridTheme.colors.rowHover, '--ag-selected-row-background-color': gridTheme.colors.selectedRow, '--ag-border-color': gridTheme.colors.border, '--ag-header-foreground-color': gridTheme.colors.headerForeground, '--ag-foreground-color': gridTheme.colors.foreground } as React.CSSProperties}>
                    <AgGridReact
                      rowData={result.rows}
                      columnDefs={columnDefs}
                      suppressMovableColumns={false}
                      suppressCellFocus={false}
                      animateRows={false}
                    />
                  </div>
                )}
                {/* Status bar */}
                <div className="flex items-center justify-between px-3 h-[28px] bg-dbgo-panel border-t flex-shrink-0">
                  <div className="flex items-center gap-3 text-[11px] text-text-secondary">
                    <span>
                      Rows: <span className="text-text-primary font-mono">{result.rowCount}</span>
                    </span>
                    <span>
                      Time: <span className="text-text-primary font-mono">{result.durationMs}ms</span>
                    </span>
                    {result.columns.length > 0 && (
                      <span>
                        Columns: <span className="text-text-primary font-mono">{result.columns.length}</span>
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowExportModal(true)}
                    disabled={!result.rows.length}
                    className="flex items-center gap-1 text-[11px] text-text-secondary hover:text-text-primary disabled:opacity-30 rounded-sm px-1.5 py-0.5 hover:bg-dbgo-tab-hover transition-colors"
                    title="Export results"
                  >
                    <Download size={11} />
                    Export
                  </button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Running indicator */}
      {isExecuting && (
        <div className="absolute inset-x-0 top-[38px] h-[2px] bg-accent-blue animate-pulse" />
      )}

      {/* Editor context menu */}
      {editorContextMenu && (
        <ContextMenu
          items={editorContextMenuItems}
          position={editorContextMenu}
          onClose={() => setEditorContextMenu(null)}
        />
      )}

      {showExportModal && activeQueryResult && (
        <ExportModal
          columns={activeQueryResult.columns.map((c) => ({ name: c.name, type: c.type }))}
          rows={activeQueryResult.rows}
          defaultFilename="query_result"
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* JSON viewer — opened from a JsonChipRenderer double-click */}
      {jsonViewer && (
        <JsonViewerModal
          value={jsonViewer.value}
          title={jsonViewer.title}
          onClose={() => setJsonViewer(null)}
        />
      )}
    </div>
  );
}
